import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTasks, useCreateTaskRelation, useDeleteTaskRelation } from '@hooks';

const RELATION_TYPES = {
  BLOCKS: { label: 'Bloquea a', icon: '🚫', color: '#f59e0b' },
  RELATES_TO: { label: 'Relacionada con', icon: '🔗', color: '#3b82f6' },
};

function RelationChip({ task, type, onRemove }) {
  const colors = {
    blocking: { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' },
    blockedBy: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
    relatedTo: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  };

  const color = colors[type];

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '16px',
        backgroundColor: color.bg,
        border: `1px solid ${color.border}`,
        fontSize: '12px',
        color: color.text,
      }}
    >
      <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {task.title}
      </span>
      <button
        type="button"
        onClick={onRemove}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0 2px',
          color: color.text,
          fontWeight: 'bold',
          fontSize: '14px',
          lineHeight: 1,
        }}
      >
        x
      </button>
    </div>
  );
}

RelationChip.propTypes = {
  task: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default function TaskRelationSelector({
  taskId,
  projectId,
  currentRelations = { blocking: [], blockedBy: [], relatedTo: [] },
  onRelationChange,
  compact = false,
}) {
  const [activeTab, setActiveTab] = useState('BLOCKS');
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: tasksData } = useTasks({ projectId }, { enabled: !!projectId });
  const createRelation = useCreateTaskRelation();
  const deleteRelation = useDeleteTaskRelation();

  const availableTasks = useMemo(() => {
    if (!tasksData?.data) return [];

    const relatedIds = new Set([
      taskId,
      ...currentRelations.blocking.map((r) => r.task.id),
      ...currentRelations.blockedBy.map((r) => r.task.id),
      ...currentRelations.relatedTo.map((r) => r.task.id),
    ]);

    return tasksData.data
      .filter((t) => !relatedIds.has(t.id))
      .filter((t) => t.title.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [tasksData, taskId, currentRelations, searchTerm]);

  const handleAddRelation = async (targetTaskId) => {
    try {
      await createRelation.mutateAsync({
        sourceTaskId: taskId,
        targetTaskId,
        type: activeTab,
      });
      setSearchTerm('');
      onRelationChange?.();
    } catch (error) {
      console.error('Error adding relation:', error);
    }
  };

  const handleRemoveRelation = async (relationId) => {
    try {
      await deleteRelation.mutateAsync(relationId);
      onRelationChange?.();
    } catch (error) {
      console.error('Error removing relation:', error);
    }
  };

  const labelStyle = {
    display: 'block',
    fontSize: compact ? '12px' : '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: compact ? '6px' : '8px',
  };

  const hasRelations =
    currentRelations.blocking.length > 0 ||
    currentRelations.blockedBy.length > 0 ||
    currentRelations.relatedTo.length > 0;

  const totalRelations =
    currentRelations.blocking.length +
    currentRelations.blockedBy.length +
    currentRelations.relatedTo.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '12px' : '16px' }}>
      {/* Header con toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ ...labelStyle, marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          Relaciones
          {totalRelations > 0 && (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '10px',
              backgroundColor: '#e0e7ff',
              color: '#4f46e5',
              fontWeight: '500',
            }}>
              {totalRelations}
            </span>
          )}
        </label>
        {compact && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              fontSize: '12px',
              color: '#6366f1',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
            }}
          >
            {isExpanded ? 'Cerrar' : 'Agregar'}
          </button>
        )}
      </div>

      {/* Current Relations Display - siempre visible si hay relaciones */}
      {hasRelations && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {currentRelations.blockedBy.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: compact ? '8px' : '10px',
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              border: '1px solid #fecaca',
            }}>
              <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600', minWidth: '70px' }}>
                Bloqueada por
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1 }}>
                {currentRelations.blockedBy.map((rel) => (
                  <RelationChip
                    key={rel.id}
                    task={rel.task}
                    type="blockedBy"
                    onRemove={() => handleRemoveRelation(rel.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {currentRelations.blocking.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: compact ? '8px' : '10px',
              backgroundColor: '#fffbeb',
              borderRadius: '8px',
              border: '1px solid #fde68a',
            }}>
              <span style={{ fontSize: '11px', color: '#d97706', fontWeight: '600', minWidth: '70px' }}>
                Bloquea a
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1 }}>
                {currentRelations.blocking.map((rel) => (
                  <RelationChip
                    key={rel.id}
                    task={rel.task}
                    type="blocking"
                    onRemove={() => handleRemoveRelation(rel.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {currentRelations.relatedTo.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: compact ? '8px' : '10px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px',
              border: '1px solid #bfdbfe',
            }}>
              <span style={{ fontSize: '11px', color: '#2563eb', fontWeight: '600', minWidth: '70px' }}>
                Relacionada
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1 }}>
                {currentRelations.relatedTo.map((rel) => (
                  <RelationChip
                    key={rel.id}
                    task={rel.task}
                    type="relatedTo"
                    onRemove={() => handleRemoveRelation(rel.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add New Relation - collapsible en modo compact */}
      {(!compact || isExpanded) && (
        <div style={{
          padding: compact ? '12px' : '0',
          backgroundColor: compact ? '#f8fafc' : 'transparent',
          borderRadius: compact ? '8px' : '0',
          border: compact ? '1px solid #e2e8f0' : 'none',
        }}>
          {/* Relation Type Tabs - más compactos */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            {Object.entries(RELATION_TYPES).map(([type, config]) => (
              <button
                key={type}
                type="button"
                onClick={() => setActiveTab(type)}
                style={{
                  padding: compact ? '5px 10px' : '6px 12px',
                  borderRadius: '6px',
                  border: activeTab === type ? `2px solid ${config.color}` : '1px solid #e2e8f0',
                  backgroundColor: activeTab === type ? `${config.color}15` : 'white',
                  color: activeTab === type ? config.color : '#64748b',
                  fontSize: '12px',
                  fontWeight: activeTab === type ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span>{config.icon}</span>
                <span>{config.label}</span>
              </button>
            ))}
          </div>

          {/* Search Input */}
          <input
            type="text"
            placeholder="Buscar tarea..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: compact ? '8px 10px' : '10px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              fontSize: '13px',
              marginBottom: '6px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          {/* Task List - más compacto */}
          <div
            style={{
              maxHeight: compact ? '140px' : '180px',
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              backgroundColor: 'white',
            }}
          >
            {availableTasks.length === 0 ? (
              <p style={{ padding: '12px', color: '#94a3b8', fontSize: '12px', textAlign: 'center', margin: 0 }}>
                {searchTerm ? 'No se encontraron tareas' : 'No hay tareas disponibles'}
              </p>
            ) : (
              availableTasks.slice(0, 8).map((task) => (
                <div
                  key={task.id}
                  onClick={() => handleAddRelation(task.id)}
                  style={{
                    padding: compact ? '8px 10px' : '10px 12px',
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    transition: 'background-color 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                >
                  <span style={{
                    fontSize: '12px',
                    color: '#334155',
                    flex: 1,
                    marginRight: '8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {task.title}
                  </span>
                  <span
                    style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: '#f1f5f9',
                      color: '#64748b',
                      flexShrink: 0,
                    }}
                  >
                    {task.status}
                  </span>
                </div>
              ))
            )}
          </div>

          {createRelation.isPending && (
            <p style={{ fontSize: '11px', color: '#6366f1', marginTop: '6px', margin: '6px 0 0 0' }}>
              Agregando...
            </p>
          )}

          {createRelation.isError && (
            <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '6px', margin: '6px 0 0 0' }}>
              {createRelation.error?.response?.data?.message || 'Error al agregar'}
            </p>
          )}
        </div>
      )}

      {/* Mostrar hint cuando no hay relaciones y está colapsado */}
      {compact && !isExpanded && !hasRelations && (
        <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
          Sin relaciones
        </p>
      )}
    </div>
  );
}

TaskRelationSelector.propTypes = {
  taskId: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  currentRelations: PropTypes.shape({
    blocking: PropTypes.array,
    blockedBy: PropTypes.array,
    relatedTo: PropTypes.array,
  }),
  onRelationChange: PropTypes.func,
  compact: PropTypes.bool,
};
